import { type AgentRunDetail, DEPLOY_PRODUCTION_APPROVAL, isActiveAgentRunStatus } from '@fos/shared/contracts/agents';
import { approvalCardPath } from '@fos/shared/contracts/approvals';
import type { TaskAiState } from '@fos/shared/contracts/work';
import { AGENT_DEPLOY_SEPARATE_NOTE, AGENT_RUN_FAILURE_LABELS, agentRunBlockTitle } from '@fos/shared/labels';
import { Check, Rocket } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { formatRelativePast } from '../../../../lib/format';
import { trpc } from '../../../../lib/trpc';
import { useAccess } from '../../../../lib/useAccess';
import { AIBlock, Button, ErrorState, Skeleton } from '../../../../ui';
import { AcceptRunDialog, RequestDeployDialog } from '../../../agents/components/RunDialogs';
import { runPath, summaryExcerpt, testsLabel } from '../../../agents/format';

/**
 * Blok wyniku AI na karcie zadania (Figma 04 „AI skończyło pracę”): ostatni przebieg zadania — co zrobiono (1–2
 * zdania), Primary „Odbierz wynik”, Secondary „Zobacz zmiany”, notka „Wdrożenie na produkcję to osobna decyzja…”.
 * W toku: „AI pracuje” z linkiem do przebiegu; po odbiorze: „Poproś o wdrożenie”; błąd: powód. Brak runów = nic.
 *
 * Kompozycja: `<AiResultBlock taskId={task.id} aiState={task.aiState} />` w miejscu dotychczasowego AIBlock karty.
 * `aiState` (z zadania) służy tylko do odświeżenia po zmianie stanu; dane runu przychodzą z `agents.latestForTask`.
 */
export function AiResultBlock({
  taskId,
  aiState,
  className,
}: {
  taskId: string;
  aiState?: TaskAiState;
  className?: string;
}) {
  const access = useAccess();
  const enabled = access.can('agents');
  const latest = trpc.agents.latestForTask.useQuery(
    { taskId },
    {
      enabled,
      refetchInterval: (q) => {
        const s = q.state.data?.status;
        return s === 'queued' || (s && isActiveAgentRunStatus(s)) ? 10_000 : false;
      },
    },
  );
  if (!enabled) return null;
  if (latest.isLoading) {
    return (
      <Skeleton
        className={`h-36 w-full rounded-xl ${className ?? ''}`}
        aria-label={`Wczytywanie wyniku AI (${aiState ?? ''})`}
      />
    );
  }
  if (latest.isError) {
    return (
      <div className={className}>
        <ErrorState compact title="Nie udało się wczytać wyniku AI" onRetry={() => void latest.refetch()} />
      </div>
    );
  }
  if (!latest.data) return null;
  return <RunBlock run={latest.data} className={className} />;
}

/** Stan prośby o wdrożenie w bloku wyniku: „Wdrożenie czeka na akceptację” / „Wdrożono” / „…nie powiodło się”. */
function deployStateOf(a: AgentRunDetail['approvals'][number]): { title: string; pending: boolean } | null {
  if (a.status === 'pending') return { title: 'Wdrożenie czeka na akceptację', pending: true };
  if (a.status === 'approved' || a.status === 'executing') return { title: 'Wdrożenie w toku', pending: false };
  if (a.status === 'succeeded') return { title: a.simulated ? 'Wdrożono · symulacja' : 'Wdrożono', pending: false };
  if (a.status === 'failed') return { title: 'Wdrożenie nie powiodło się', pending: false };
  return null;
}

function RunBlock({ run, className }: { run: AgentRunDetail; className?: string }) {
  const access = useAccess();
  const [accepting, setAccepting] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const display = run.displayStatus;
  const when = run.finishedAt ?? run.startedAt ?? run.createdAt;
  const tests = testsLabel(run.tests);

  let body: string | null = null;
  if (display === 'to_review' || display === 'succeeded') body = summaryExcerpt(run.summary);
  else if (display === 'failed')
    body = run.failureReason ?? (run.failureCode ? AGENT_RUN_FAILURE_LABELS[run.failureCode] : null);
  else if (display === 'queued')
    body =
      run.runtime === 'external'
        ? 'Zlecenie czeka, aż sesja Claude przyjmie je przez MCP.'
        : 'Zlecenie czeka, aż runner na Twoim komputerze je odbierze.';
  else if (display === 'running')
    body = run.cancelRequestedAt
      ? 'Poproszono o przerwanie — runner kończy pracę.'
      : 'Pracuje nad zadaniem. Dziennik zobaczysz w przebiegu.';
  else if (display === 'waiting_for_approval') body = 'Agent czeka na decyzję w Akceptacjach.';
  else if (display === 'cancelled') body = run.failureReason;

  // kompozycja Fali 5: po odbiorze stan wdrożenia z akceptacji `deploy.production` (ostatnia prośba)
  const deploy = [...run.approvals].reverse().find((a) => a.type === DEPLOY_PRODUCTION_APPROVAL) ?? null;
  const deployState = display === 'succeeded' && deploy ? deployStateOf(deploy) : null;
  const canOpenApproval = deployState !== null && access.can('approvals');

  const title = (
    <>
      {deployState?.title ?? agentRunBlockTitle(display, run.runtime)}
      <span className="ml-2 font-normal text-fg-muted">· {formatRelativePast(when)}</span>
    </>
  );

  const actions = (
    <>
      {run.permissions.accept && (
        <Button variant="primary" icon={Check} onClick={() => setAccepting(true)}>
          Odbierz wynik
        </Button>
      )}
      {run.permissions.requestDeploy && (
        <Button variant="secondary" icon={Rocket} onClick={() => setDeploying(true)}>
          Poproś o wdrożenie
        </Button>
      )}
      {canOpenApproval && deploy && (
        <Button asChild variant={deployState?.pending ? 'primary' : 'secondary'}>
          <Link to={approvalCardPath(deploy.id)}>Otwórz akceptację</Link>
        </Button>
      )}
      <Button asChild variant="secondary">
        <Link to={runPath(run.id, display === 'to_review' || display === 'succeeded' ? 'zmiany' : undefined)}>
          {display === 'to_review' || display === 'succeeded' ? 'Zobacz zmiany' : 'Zobacz przebieg'}
        </Link>
      </Button>
    </>
  );

  return (
    <>
      <AIBlock
        tone="subtle"
        className={`p-6 ${className ?? ''}`}
        title={title}
        actions={actions}
        footnote={
          display === 'to_review' ? AGENT_DEPLOY_SEPARATE_NOTE : tests && display === 'succeeded' ? tests : undefined
        }
      >
        {body && <p className="text-body-m text-fg-secondary">{body}</p>}
        {tests && display === 'to_review' && <p className="mt-1 text-caption text-fg-muted">{tests}</p>}
      </AIBlock>
      <AcceptRunDialog run={run} open={accepting} onOpenChange={setAccepting} />
      <RequestDeployDialog run={run} open={deploying} onOpenChange={setDeploying} />
    </>
  );
}
